import { HugeiconsIcon } from "@hugeicons/react"
import { LockIcon } from "@hugeicons/core-free-icons"
import { Button, type ButtonProps } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { getPlanDisplayName } from '@/lib/constants/pricing'
import { cn } from '@/lib/utils'
import { inferPlanLimitReason, buildUpgradeTooltip } from './planLimitCopy'

interface CreateOrUpgradeButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** True when the plan limit is reached for this feature. */
  atLimit: boolean
  /** Called on click when under the limit. Ignored (and never fires) at limit. */
  onClick: () => void
  /** Label shown when under the limit, e.g. "Add page" / "New Funnel". */
  children: React.ReactNode
  /** Plural noun for the at-limit tooltip, e.g. "experiments". */
  noun?: string
  /** Plan cap shown in the at-limit tooltip. */
  limit?: number
  /** Override the named plan in the tooltip; defaults to the current subscription. */
  planName?: string
}

/**
 * Create-entry-point button that turns into an explicit Upgrade CTA once the
 * plan limit is reached, instead of silently no-oping or staying disabled
 * with no explanation (the heatmaps "Add page" button used to do nothing at
 * the limit).
 */
export function CreateOrUpgradeButton({
  atLimit,
  onClick,
  children,
  className,
  noun,
  limit,
  planName: planNameOverride,
  ...rest
}: CreateOrUpgradeButtonProps) {
  const { openPricingModal, isReadOnly, readOnlyReason, currentPlan, planName } = useSubscription()
  const displayPlanName = planNameOverride || getPlanDisplayName(currentPlan, planName)
  // A canceled (read-only) subscription is an ended plan, not a usage cap — don't
  // seed the pricing modal with a "you've used all {noun}" limit header.
  const reason = isReadOnly ? undefined : inferPlanLimitReason(noun)

  if (atLimit) {
    // At a plan limit the button stays the view's one orange CTA. After a
    // cancel it turns quiet (outline + lock): the read-only strip owns the ask.
    const button = isReadOnly ? (
      <Button
        variant="outline"
        onClick={() => openPricingModal(reason ? { reason } : undefined)}
        className={cn('gap-1.5 whitespace-nowrap', className)}
        {...rest}
      >
        <HugeiconsIcon icon={LockIcon} size={12} className="text-muted-foreground" />
        {readOnlyReason === 'trial_ended' ? 'Reactivate' : 'Upgrade'}
      </Button>
    ) : (
      <Button
        onClick={() => openPricingModal(reason ? { reason } : undefined)}
        className={cn('whitespace-nowrap', className)}
        {...rest}
      >
        Upgrade
      </Button>
    )

    if (!noun) return button

    const tooltip = buildUpgradeTooltip({
      noun,
      limit,
      planName: displayPlanName,
      isReadOnly,
      readOnlyReason,
    })

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Button onClick={onClick} className={cn('whitespace-nowrap', className)} {...rest}>
      {children}
    </Button>
  )
}
